import { useNavigate } from 'react-router-dom';
import { Globe, Users, MessageCircle, Heart, Sparkles, Shield, Smartphone, Download } from 'lucide-react';
import { QRCodeCanvas } from 'qrcode.react';

const Landing = () => {
  const navigate = useNavigate();
  const appUrl = window.location.origin;

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-peach-50 to-teal-50">
      {/* Hero Section */}
      <nav className="glass fixed top-0 left-0 right-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-2">
            <Globe className="w-8 h-8 text-coral" />
            <h1 className="text-2xl font-bold text-gray-900">CultraMeet</h1>
          </div>
          <button
            data-testid="get-started-btn"
            onClick={() => navigate('/auth')}
            className="btn-primary"
          >
            Get Started
          </button>
        </div>
      </nav>

      <div className="pt-32 pb-20 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <div className="animate-fadeIn">
            <h2 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-gray-900 mb-6">
              Meet <span className="text-coral">Amazing People</span>
              <br />Around the <span className="text-teal">World</span>
            </h2>
            <p className="text-lg sm:text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              Connect with travelers, adventurers, and lifelong friends. Share your lifestyle,
              discover new cultures, and create unforgettable memories together.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                data-testid="explore-btn"
                onClick={() => navigate('/auth')}
                className="btn-primary text-lg px-8 py-4"
              >
                Start Exploring
              </button>
              <button
                onClick={() => navigate('/auth')}
                className="btn-secondary text-lg px-8 py-4"
              >
                Learn More
              </button>
            </div>
          </div>

          {/* Features Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-20">
            <div className="glass rounded-3xl p-8 card-hover animate-slideIn border-2 border-teal" style={{animationDelay: '0.1s'}}>
              <div className="bg-white w-16 h-16 rounded-2xl flex items-center justify-center mb-4 mx-auto border-2 border-teal">
                <Users className="w-8 h-8 text-black" />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-black">Discover People</h3>
              <p className="text-black">
                Browse profiles of travelers and locals worldwide. Find people who share your interests and passions.
              </p>
            </div>

            <div className="glass rounded-3xl p-8 card-hover animate-slideIn border-2 border-teal" style={{animationDelay: '0.2s'}}>
              <div className="bg-white w-16 h-16 rounded-2xl flex items-center justify-center mb-4 mx-auto border-2 border-teal">
                <MessageCircle className="w-8 h-8 text-black" />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-black">Connect & Chat</h3>
              <p className="text-black">
                Send messages, share your stories, and build meaningful connections that last a lifetime.
              </p>
            </div>

            <div className="glass rounded-3xl p-8 card-hover animate-slideIn border-2 border-teal" style={{animationDelay: '0.3s'}}>
              <div className="bg-white w-16 h-16 rounded-2xl flex items-center justify-center mb-4 mx-auto border-2 border-teal">
                <Heart className="w-8 h-8 text-black" />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-black">Share Lifestyle</h3>
              <p className="text-black">
                Post photos, share experiences, and inspire others with your unique lifestyle and adventures.
              </p>
            </div>
          </div>

          {/* AI & Safety Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-16">
            <div className="glass rounded-3xl p-8 text-left">
              <Sparkles className="w-12 h-12 text-coral mb-4" />
              <h3 className="text-3xl font-bold mb-4">AI-Powered Matching</h3>
              <p className="text-gray-600 text-lg">
                Our intelligent algorithm suggests the best matches based on your interests,
                location, and lifestyle preferences. Find your perfect travel buddy or lifelong friend.
              </p>
            </div>

            <div className="glass rounded-3xl p-8 text-left">
              <Shield className="w-12 h-12 text-teal mb-4" />
              <h3 className="text-3xl font-bold mb-4">Safe & Secure</h3>
              <p className="text-gray-600 text-lg">
                Your safety is our priority. Report inappropriate behavior, block users,
                and enjoy AI-powered content moderation to keep the community friendly.
              </p>
            </div>
          </div>

          {/* CTA Section */}
          <div className="mt-20 glass rounded-3xl p-12">
            <h3 className="text-4xl font-bold mb-4">Ready to Start Your Journey?</h3>
            <p className="text-xl text-gray-600 mb-8">
              Join thousands of travelers and make connections that matter.
            </p>
            <button
              data-testid="join-now-btn"
              onClick={() => navigate('/auth')}
              className="btn-primary text-xl px-12 py-5"
            >
              Join Now - It's Free!
            </button>
          </div>
        </div>
      </div>

      {/* QR Code Download Section */}
      <div className="py-20 px-4 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <div className="mb-8">
            <div className="flex items-center justify-center space-x-3 mb-4">
              <Smartphone className="w-10 h-10 text-teal" />
              <h2 className="text-4xl font-bold">Install CultraMeet on Your Phone</h2>
            </div>
            <p className="text-gray-600 text-lg">
              Scan the QR code below with your phone's camera to install the app
            </p>
          </div>

          <div className="glass rounded-3xl p-8 inline-block">
            <div className="bg-white p-6 rounded-2xl shadow-lg mb-6">
              <QRCodeCanvas
                value={appUrl}
                size={256}
                level="H"
                includeMargin={true}
                fgColor="#14B8A6"
                bgColor="#FFFFFF"
              />
            </div>
            
            <div className="space-y-4 text-left max-w-md mx-auto">
              <h3 className="text-xl font-bold text-center mb-4">How to Install:</h3>
              
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-teal text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">1</div>
                <div>
                  <p className="font-semibold">Scan QR Code</p>
                  <p className="text-sm text-gray-600">Open your phone's camera and point it at the QR code</p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-teal text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">2</div>
                <div>
                  <p className="font-semibold">Open Link</p>
                  <p className="text-sm text-gray-600">Tap the notification to open CultraMeet in your browser</p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-teal text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">3</div>
                <div>
                  <p className="font-semibold">Install App</p>
                  <p className="text-sm text-gray-600">
                    <strong>iOS:</strong> Tap Share → "Add to Home Screen"<br />
                    <strong>Android:</strong> Tap menu → "Install App"
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-6 p-4 bg-teal/10 rounded-xl">
              <p className="text-sm text-gray-700">
                <Download className="w-4 h-4 inline mr-1" />
                Works on iPhone, Android, and any smartphone with a camera
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <Globe className="w-6 h-6" />
            <span className="text-xl font-bold">CultraMeet</span>
          </div>
          <p className="text-gray-400 mb-2">© 2025 CultraMeet. Connect with the world.</p>
          <div className="flex items-center justify-center space-x-4 text-sm">
            <button 
              onClick={() => window.open('/contact', '_blank')}
              className="text-teal hover:text-teal-light transition-colors underline cursor-pointer"
            >
              Contact Support
            </button>
            <span className="text-gray-600">|</span>
            <span className="text-gray-400">18+ Only</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;